import { Injectable, OnDestroy } from '@angular/core';

import { MessageService } from 'primeng/api';
import { XMLParser } from 'fast-xml-parser';
import { BehaviorSubject, catchError, concatMap, filter, from, map, Observable, of, Subscription, tap, toArray } from 'rxjs';
import moment from 'moment-timezone';
import { isNumber } from 'underscore';

import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { MentionModel } from '../../../../api/mentions/model/mention.model';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';

import { Author } from '../../models/author';
import { MediaTypeModel } from '../../models/media-type.model';
import { MentionImportFileParameters, MentionImportRequestModel, MentionImportResponseModel } from '../../services/mention-csv-import-dialog.service';
import { Publication } from '../../models/publication';
import { ProgressModel } from '../../../../Common/Dialogs/models/progress.model';


export interface MentionImportArticle {
    fileName: string;
    mentionRaw: any;
    errors?: string[];
    mention?: Mention;
}

@Injectable()
export class NewsdeskMentionImportService implements OnDestroy {

    articles: MentionImportArticle[] = [];
    failedArticles: MentionImportArticle[] = [];
    userPreferenceTimezone: string;

    parser = new XMLParser();
    subscriptions: Subscription[] = [];

    progress: ProgressModel;
    private progress$ = new BehaviorSubject<ProgressModel>(null);

    readonly mediaTypesMap = new Map<string, MediaTypeEnum>([
        [ 'newspapers', MediaTypeEnum.PRINT         ],
        [ 'magazine',   MediaTypeEnum.PRINT         ],
        [ 'television', MediaTypeEnum.TV            ],
        [ 'rss feed',   MediaTypeEnum.ONLINE_NEWS   ],
        [ 'web',        MediaTypeEnum.ONLINE_NEWS   ],
        [ 'newswire',   MediaTypeEnum.PRESS_RELEASE ],
    ]);
    readonly importBatchSize: number = 200;

    constructor(
        public mentionsApi: MentionsApi,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    // default value for fallback timezone is user preference timezone
                    this.userPreferenceTimezone = preferences.timeZone;
                }
            )
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    validateFile(file: File): Observable<MentionImportArticle[] | boolean> {
        return from(file.arrayBuffer()).pipe(
            map(content => {
                const textContent = new TextDecoder().decode(new Uint8Array(content));
                const summaries = this.parser.parse(textContent)?.result?.articles?.article;

                if (summaries?.length > 0) {
                    return summaries.map(article => {
                        return { fileName: file.name, mentionRaw: article, errors: [] } as MentionImportArticle;
                    });
                }   else if (summaries) {
                    // There is one article, summaries is an object instead of an array
                    return [{ fileName: file.name, mentionRaw: summaries, errors: [] }] as MentionImportArticle[];
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform(
                            'Monitoring.mentionImporter.fileFormatNotSupported',
                            { fileName: file.name }
                        )
                    });

                    return false;
                }
            }),
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Monitoring.mentionImporter.readFileError')
                });

                return of(false);
            }));
    }

    // Process each article and turn them into mentionModels. Put any into failedArticles if it does not have a valid required field value
    addArticles(articles: MentionImportArticle[]): void {
        articles.forEach(article => {
            if (!this.validateArticle(article)) {
                this.failedArticles.push(article);
                return;
            }
            this.articles.push(article);
        });
        const failedArticlesMessageList = this.failedArticles.map(article => {
            return 'Article id = ' + article.mentionRaw.id + ': ' + article.errors.join(', ');
        });

        if (this.failedArticles.length > 0) {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    heading: 'Monitoring.mentionImporter.newsdesk.mentionsNotImported.heading',
                    message: this.localizationService.transform(
                        'Monitoring.mentionImporter.newsdesk.mentionsNotImported.message',
                        { number: failedArticlesMessageList.length }
                    ),
                    list: failedArticlesMessageList,
                },
                outputs: {
                    dialogClose: (ok: boolean) => {
                        this.failedArticles = [];
                    }
                }
            });
        }
    }

    removeArticles(file: File): void {
        this.articles = this.articles.filter(article => article.fileName !== file.name);
    }

    validateArticle(article: MentionImportArticle): boolean {
        const a = article.mentionRaw;
        if (!a.title) {
            article.errors.push(this.localizationService.transform('Monitoring.mentionImporter.newsdesk.titleMissing'));
        }
        if (!a.media?.name) {
            article.errors.push(this.localizationService.transform('Monitoring.mentionImporter.newsdesk.mediaMissing'));
        } else if (!this.mediaTypesMap.get(a.media.name?.toLocaleLowerCase())) {
            article.errors.push(this.localizationService.transform('Monitoring.mentionImporter.newsdesk.invalidMedia'));
        }

        return article.errors.length === 0;
    }

    setMentionsFromRawArticles(): void {
        this.articles.forEach(article => {
            this.setMentionFromRawArticle(article);
        });
    }

    setMentionFromRawArticle(article: MentionImportArticle): void {
        const a = article.mentionRaw;
        const mention = new Mention();

        mention.headline = a.title;
        const mediaType = this.mediaTypesMap.get(a.media.name.toLocaleLowerCase());
        if (mediaType) {
            mention.mediaType = new MediaTypeModel(mediaType);
        }
        if (a.publicationDate && moment(a.publicationDate).isValid()) {
            const datePublished = moment(a.publicationDate);
            if (a.publicationDate.indexOf(':') === -1) {
                // no time data
                datePublished.hour(new Date().getUTCHours());
                datePublished.minute(new Date().getUTCMinutes());
            }
            mention.datePublished = datePublished.toDate();
        } else {
            mention.datePublished = new Date();
        }

        mention.body = a.body
            .replace(/(<\/p>|<br>)/g, ' \n\n ')
            .replace(/<\/?[^>]+>/g, '');
        if (a.byline) {
            mention.author = {
                name: a.byline
            } as Author;
        }
        if (a.publication?.name) {
            mention.publication = {
                name: a.publication.name
            } as Publication;
        }
        if (a.articleUrl) {
            mention.url = a.articleUrl;
        } else if (a.url) {
            mention.url = a.url;
        }
        if (a.originalArticleUrl) {
            mention.originalUrl = a.originalArticleUrl;
        }
        if (a.tone > 0) {
            mention.sentiment = 1;
        } else if (a.tone < 0) {
            mention.sentiment = -1;
        } else if (a.tone === 0) {
            mention.sentiment = 0;
        }
        if (a.wordCount && isNumber(a.wordCount) || a.wordCount === 0) {
            mention.wordCount = a.wordCount;
        }
        if (a.lead) {
            mention.summary = a.lead.replace(/<\/?[^>]+>/g, '');
        }
        // keywords

        article.mention = mention;
    }

    importData(fileParameters: MentionImportFileParameters & { fileName: string }): Observable<MentionImportResponseModel[]> {
        const mentionModels: MentionModel[] = this.articles.map(article => {
            const mention = article.mention;
            if (fileParameters.folders) {
                mention.folderIds = fileParameters.folders.map(folder => folder.id);
            }
            if (fileParameters.tags) {
                mention.tagIds = fileParameters.tags.map(tag => tag.id);
            }
            return mention.convertToApiMention();
        });

        const apiCalls: Observable<MentionImportResponseModel>[] = [];
        for (let i = 0; i < mentionModels.length; i += this.importBatchSize) {
            const batch = mentionModels.slice(i, i + this.importBatchSize);
            const uniqueFolderIds = new Set<number>(batch.map(b => b?.folderIds ?? []).flat());
            const uniqueTagIds = new Set<number>(batch.map(b => b?.tagIds ?? []).flat());
            const request: MentionImportRequestModel = {
                mentionModels: batch,
                fileName: fileParameters.fileName,
                folderIds: Array.from(uniqueFolderIds),
                tagIds: Array.from(uniqueTagIds),
            };
            apiCalls.push(this.mentionsApi.bulkImport(request));
        }

        this.progress = {
            currentIndex: 0,
            totalItems: apiCalls.length
        };
        this.progress$.next(this.progress);

        return from(apiCalls).pipe(
            concatMap((request) => request.pipe(
                tap(() => {
                    this.progress = {
                        currentIndex: ++this.progress.currentIndex,
                        totalItems: this.progress.totalItems
                    };
                    this.progress$.next(this.progress);
                })
            )),
            toArray()
        );
    }

    clearImportedFiles(): void {
        this.articles = [];
        this.failedArticles = [];
    }

    getArticlesLength(): number {
        return this.articles.length;
    }

    getProgress() {
        return this.progress$;
    }
}
